import React, {useEffect, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {IStore} from 'redux/interface';
import {Pages, RoutesUrl, TranslationKeys} from 'utils/constants/routeConstants';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {
  getAssistantSession,
  getPatientAssignments,
  getPatientCareTeam,
  getPatientMedications,
  getPatientRun,
  getTranslations,
  interventionAsyncActions
} from 'redux/pageAsyncActions';

import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';

import {ReactComponent as InterventionIcon} from 'assets/image/menu-auth/intervention.svg';
import NotFoundImg from 'assets/image/common/notFound.png';

import {IPatientMedication, IPatientPendingEntry, InterventionStatusCode} from '../../redux/intervention/interfaces';
import {useInterventionSse} from '../../utils/hooks/useInterventionSse';

import './styles.scss';

const STATUS_LABELS: Record<InterventionStatusCode, string> = {
  enrolled: 'Enrolled',
  in_progress: 'In progress',
  screening: 'Screening',
  paused: 'Paused',
  requested: 'Requested',
  completed: 'Completed',
  withdrawn: 'Withdrawn',
  screen_failed: 'Screen Failed'
};

function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }

  return new Date(iso).toLocaleDateString();
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'});
}

const PendingEntries = ({entries}: {entries: IPatientPendingEntry[]}) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="intervention-card__pending">
      <div className="intervention-card__pending-title">
        <FormattedMessage id="gritx.interventions.pending" defaultMessage="Pending"/>
      </div>
      <ul className="pending-entry-list">
        {entries.map((entry) => {
          if (entry.type === 'step') {
            return (
              <li key={`step-${entry.pasId}`} className="pending-entry">
                <div className="pending-entry__row">
                  <span className="pending-entry__title">{entry.moduleTitle}</span>
                  {entry.isScreener && (
                    <span className="pending-entry__badge">
                      <FormattedMessage id="gritx.interventions.screener" defaultMessage="Screener"/>
                    </span>
                  )}
                </div>
                <div className="pending-entry__meta">
                  <span className="pending-entry__program">{entry.programName}</span>
                  {entry.scheduledFor && (
                    <span className="pending-entry__time">{formatDateTime(entry.scheduledFor)}</span>
                  )}
                </div>
              </li>
            );
          }

          return (
            <li key={`med-${entry.medicationLogId}`} className="pending-entry pending-entry--medication">
              <div className="pending-entry__row">
                <span className="pending-entry__title">{entry.drugName}</span>
                <span className="pending-entry__dosage">{entry.dosage}</span>
              </div>
              <div className="pending-entry__meta">
                <span className="pending-entry__time">{formatDateTime(entry.scheduledAt)}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const MedicationsList = ({medications}: {medications: IPatientMedication[]}) => {
  if (medications.length === 0) {
    return null;
  }

  return (
    <section className="interventions__section">
      <h2 className="interventions__section-title">
        <FormattedMessage id="gritx.interventions.medications" defaultMessage="Medications"/>
      </h2>
      <ul className="interventions__medications">
        {medications.map((m) => (
          <li key={m.patientMedicationId} className="medication-item">
            <span className="medication-item__name">{m.drugName}</span>
            <span className="medication-item__dosage">{m.dosage}</span>
            {m.frequency && <span className="medication-item__freq">{m.frequency}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const Interventions: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatchedRuns = useRef(new Set<number>());

  const {settings} = useSelector((state: IStore) => state.auth);
  const {loading} = useSelector((state: IStore) => state.status);
  const {assignments, run, medications, careTeam, assistantActive} = useSelector(
    (state: IStore) => state.intervention
  );

  const pageConfig = useMemo(
    () => settings?.pages.find((p) => p.name.toLowerCase() === Pages.Intervention.toLowerCase()),
    [settings]
  );
  const translationKey = pageConfig?.translationKey || TranslationKeys.Chat;
  const title = pageConfig?.title || 'Interventions';

  useGetTranslations({translationKey});

  useEffect(() => {
    dispatch(interventionAsyncActions[getPatientAssignments]());
    dispatch(interventionAsyncActions[getPatientMedications]());
    dispatch(interventionAsyncActions[getPatientCareTeam]());
    dispatch(interventionAsyncActions[getAssistantSession]());
  }, [dispatch]);

  useInterventionSse(assignments);

  useEffect(() => {
    assignments.forEach((a) => {
      if (!dispatchedRuns.current.has(a.activityParticipantId)) {
        dispatchedRuns.current.add(a.activityParticipantId);
        dispatch(interventionAsyncActions[getPatientRun](a.activityParticipantId));
      }
    });
  }, [assignments, dispatch]);

  const isTranslationsLoading = loading.includes(getTranslations);
  const isAssignmentsLoading = loading.includes(getPatientAssignments);

  if (isTranslationsLoading) {
    return <Loader nested/>;
  }

  return (
    <div className="interventions">
      <h1 className="interventions__title">
        <InterventionIcon className="interventions__icon"/>
        <FormattedMessage
          id={`gritx.common.navigation.${title}`}
          defaultMessage={title}
        />
      </h1>

      <section className="interventions__section">
        <h2 className="interventions__section-title">
          <FormattedMessage id="gritx.interventions.yourInterventions" defaultMessage="Your Interventions"/>
        </h2>

        {isAssignmentsLoading && (
          <div className="interventions__loader">
            <Loader nested/>
          </div>
        )}

        {!isAssignmentsLoading && assignments.length === 0 && (
          <NotFoundInfo
            title={intl.formatMessage({
              id: 'gritx.interventions.noAssignments',
              defaultMessage: 'No interventions assigned yet.'
            })}
            image={NotFoundImg}
          />
        )}

        {!isAssignmentsLoading && assignments.length > 0 && (
          <div className="interventions__cards">
            {assignments.map((a) => {
              const pct = a.nonScreeningModuleCount > 0
                ? Math.round((a.executedPasCount / a.nonScreeningModuleCount) * 100)
                : 0;
              const pendingEntries = run[a.activityParticipantId];
              const runPending = pendingEntries === undefined;

              return (
                <div
                  key={a.activityParticipantId}
                  className="intervention-card"
                  style={{cursor: 'pointer'}}
                  onClick={() => navigate(RoutesUrl.ProgramDetail.replace(':apId', String(a.activityParticipantId)))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(RoutesUrl.ProgramDetail.replace(':apId', String(a.activityParticipantId)));
                    }
                  }}
                >
                  <div className="intervention-card__header">
                    <span className="intervention-card__name">{a.interventionDisplayName}</span>
                    <span className={`intervention-card__status intervention-card__status--${a.statusCode}`}>
                      {STATUS_LABELS[a.statusCode]}
                    </span>
                  </div>

                  <div className="intervention-card__progress-label">
                    {a.executedPasCount}&nbsp;/&nbsp;{a.nonScreeningModuleCount}{' '}
                    <FormattedMessage id="gritx.interventions.modules" defaultMessage="modules"/>
                  </div>
                  <div className="intervention-progress-bar">
                    <div className="intervention-progress-fill" style={{width: `${pct}%`}}/>
                  </div>

                  <div className="intervention-card__meta">
                    <span>
                      <FormattedMessage id="gritx.interventions.enrolled" defaultMessage="Enrolled"/>
                      {': '}{formatDate(a.enrolledAt)}
                    </span>
                    <span>
                      <FormattedMessage id="gritx.interventions.lastActivity" defaultMessage="Last activity"/>
                      {': '}{formatDate(a.lastActivityAt)}
                    </span>
                    {a.completedAt && (
                      <span>
                        <FormattedMessage id="gritx.interventions.completedOn" defaultMessage="Completed"/>
                        {': '}{formatDate(a.completedAt)}
                      </span>
                    )}
                  </div>

                  {runPending
                    ? <div className="intervention-card__run-loader"><Loader nested/></div>
                    : <PendingEntries entries={pendingEntries}/>
                  }
                </div>
              );
            })}
          </div>
        )}
      </section>

      <MedicationsList medications={medications}/>

      {careTeam.length > 0 && (
        <section className="interventions__section">
          <h2 className="interventions__section-title">
            <FormattedMessage id="gritx.interventions.careTeam" defaultMessage="Care Team"/>
          </h2>
          <ul className="interventions__care-team">
            {careTeam.map((m) => (
              <li key={m.userId} className="care-team-member">
                <span className="care-team-member__name">{m.firstName} {m.lastName}</span>
                <span className="care-team-member__role">{m.roleCode}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {assistantActive && (
        <button
          className="interventions__assistant-fab"
          aria-label={intl.formatMessage({
            id: 'gritx.interventions.assistant',
            defaultMessage: 'AI Assistant'
          })}
        >
          <FormattedMessage id="gritx.interventions.assistant" defaultMessage="AI Assistant"/>
        </button>
      )}
    </div>
  );
};
